import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Search, FileText, GraduationCap, CornerDownLeft } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useAuth } from '../../utils/Auth/AuthProvider';
import { getExamsList } from '../../utils/remote';
import { useStudentsList } from '../../school_panel/api/hooks';
import { sectionsForRole } from '../Sidebar/navConfig';

type Hit = { key: string; group: 'Pages' | 'Students' | 'Exams'; label: string; sub?: string; to: string; icon: LucideIcon };

const PANEL_ROLES = ['school_admin', 'teacher', 'counselor', 'school', 'admin'];
const PER_GROUP = 5;

const norm = (s: string) => s.toLowerCase().normalize('NFKD');

function useDebounced(value: string, ms: number) {
  const [v, setV] = useState(value);
  useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t); }, [value, ms]);
  return v;
}

/** Top-bar quick find: panel pages, students and exams/worksheets/homework. Ctrl/Cmd K focuses it. */
export default function GlobalSearch() {
  const { role, token } = useAuth();
  const navigate = useNavigate();
  const input = useRef<HTMLInputElement>(null);
  const [text, setText] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const q = useDebounced(text.trim(), 250);
  const isPanelUser = PANEL_ROLES.includes(role ?? '');
  const searching = open && q.length >= 2;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); input.current?.focus(); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const students = useStudentsList({}, { enabled: isPanelUser && searching });
  const exams = useQuery({
    queryKey: ['globalSearch.exams', q],
    queryFn: () => getExamsList(token, '1', '0', '0', q),
    enabled: isPanelUser && searching && !!token,
    staleTime: 30_000,
  });

  const hits = useMemo<Hit[]>(() => {
    if (q.length < 2) return [];
    const needle = norm(q);
    const pages: Hit[] = sectionsForRole(role, { alerts: 0 }).flatMap((s) => s.links
      .filter((l) => norm(l.label).includes(needle) || norm(s.heading).includes(needle))
      .map((l) => ({ key: `p${l.to}`, group: 'Pages' as const, label: l.label, sub: s.heading, to: l.to, icon: l.icon })));
    const people: Hit[] = (students.data?.results ?? [])
      .filter((r) => norm(`${r.first_name} ${r.last_name} ${r.username}`).includes(needle))
      .slice(0, PER_GROUP)
      .map((r) => ({
        key: `s${r.id}`, group: 'Students', icon: GraduationCap, to: `/students/profile/${r.id}`,
        label: `${r.first_name} ${r.last_name}`.trim() || r.username,
        sub: [r.grade_name, r.section?.display].filter(Boolean).join(' · ') || r.username,
      }));
    const examRows: { id: number; name: string; kind?: string; grade_name?: string; subject_name?: string }[] =
      Array.isArray((exams.data as any)?.exams) ? (exams.data as any).exams : [];
    const work: Hit[] = examRows.slice(0, PER_GROUP).map((e) => ({
      key: `e${e.id}`, group: 'Exams', icon: FileText, to: `/exams/exam-detail/${e.id}`, label: e.name,
      sub: [e.kind && e.kind !== 'exam' ? e.kind[0].toUpperCase() + e.kind.slice(1) : null, e.grade_name, e.subject_name].filter(Boolean).join(' · '),
    }));
    return [...pages.slice(0, PER_GROUP), ...people, ...work];
  }, [q, role, students.data, exams.data]);

  useEffect(() => { setActive(0); }, [q]);

  const go = (h: Hit) => {
    navigate(h.to);
    setOpen(false);
    setText('');
    input.current?.blur();
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') { setOpen(false); input.current?.blur(); return; }
    if (!hits.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => (i + 1) % hits.length); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => (i - 1 + hits.length) % hits.length); }
    else if (e.key === 'Enter') { e.preventDefault(); go(hits[Math.min(active, hits.length - 1)]); }
  };

  const loading = searching && (students.isLoading || exams.isLoading);
  const showPanel = open && q.length >= 2;
  const groups = (['Pages', 'Students', 'Exams'] as const).map((g) => ({ g, rows: hits.filter((h) => h.group === g) })).filter((x) => x.rows.length);

  return (
    <div className="relative max-w-[520px] flex-[1_1_320px]">
      <label className="flex min-h-[44px] items-center gap-2 rounded-[12px] border border-line-strong bg-surface px-3.5">
        <Search size={18} strokeWidth={1.8} className="flex-none text-muted" aria-hidden />
        <span className="sr-only">Search</span>
        <input ref={input} type="search" placeholder="Search students, exams, pages…" value={text}
          role="combobox" aria-expanded={showPanel} aria-controls="global-search-results" aria-autocomplete="list"
          aria-activedescendant={showPanel && hits[active] ? `gs-${hits[active].key}` : undefined}
          onChange={(e) => { setText(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          onKeyDown={onKeyDown}
          className="min-w-0 flex-1 border-0 bg-transparent p-0 text-sm text-ink shadow-none outline-none ring-0 focus:ring-0" />
        <span className="hidden whitespace-nowrap rounded-[6px] border border-line-strong px-1.5 py-0.5 font-mono text-[11px] font-semibold text-muted sm:inline-block">Ctrl K</span>
      </label>
      {showPanel && (
        <div id="global-search-results" role="listbox" aria-label="Search results"
          className="absolute left-0 right-0 top-[calc(100%+6px)] z-50 max-h-[420px] overflow-y-auto rounded-[14px] border border-line-strong bg-surface p-1.5 shadow-lg">
          {groups.map(({ g, rows }) => (
            <div key={g} className="py-1">
              <div className="px-2.5 pb-1 pt-1.5 font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-muted">{g}</div>
              {rows.map((h) => {
                const i = hits.indexOf(h);
                const Icon = h.icon;
                return (
                  <div key={h.key} id={`gs-${h.key}`} role="option" aria-selected={i === active}
                    onMouseDown={(e) => { e.preventDefault(); go(h); }} onMouseEnter={() => setActive(i)}
                    className={`flex cursor-pointer items-center gap-2.5 rounded-[10px] px-2.5 py-2 ${i === active ? 'bg-surface-2' : ''}`}>
                    <Icon size={16} strokeWidth={1.8} className="flex-none text-muted" aria-hidden />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-ink">{h.label}</span>
                      {h.sub && <span className="block truncate text-xs text-muted">{h.sub}</span>}
                    </span>
                    {i === active && <CornerDownLeft size={14} strokeWidth={1.8} className="flex-none text-muted" aria-hidden />}
                  </div>
                );
              })}
            </div>
          ))}
          {!groups.length && (
            <p className="px-3 py-4 text-sm text-muted">{loading ? 'Searching…' : `Nothing found for "${q}".`}</p>
          )}
        </div>
      )}
    </div>
  );
}
